import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Center, Text, VStack, Button } from '@chakra-ui/react';
import { FaLock } from 'react-icons/fa';
import LoadingScreen from '../common/LoadingScreen';

const ProtectedRoute = ({ 
  children, 
  requiredRole = null, 
  fallbackPath = '/login',
  showUnauthorized = true,
  redirectTo = null 
}) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <LoadingScreen height="100vh" label="Verifying authentication..." />
    );
  }

  // No token - redirect to login
  if (!token) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ 
          from: location.pathname,
          message: 'Please log in to access this page' 
        }} 
        replace 
      />
    );
  }

  // No user data - redirect to login
  if (!user) {
    return (
      <Navigate 
        to={fallbackPath} 
        state={{ 
          from: location.pathname,
          message: 'Session expired. Please log in again' 
        }} 
        replace 
      />
    );
  }

  // Role-based access control
  if (requiredRole && user.role !== requiredRole) {
    if (showUnauthorized) {
      return (
        <Center minH="100vh">
          <VStack spacing={6} textAlign="center" maxW="500px" p={8}>
            <Box color="red.500" fontSize="6xl">
              <FaLock />
            </Box>
            <Text fontSize="2xl" fontWeight="bold" color="red.500">
              Access Denied
            </Text>
            <Text color="gray.600">
              You don't have permission to access this page. 
              Required role: <strong>{requiredRole}</strong>
            </Text>
            <Text color="gray.500" fontSize="sm">
              Your current role: <strong>{user.role}</strong>
            </Text>
            <VStack spacing={3} w="100%">
              <Button 
                colorScheme="blue" 
                onClick={() => window.history.back()}
                w="100%"
              >
                Go Back
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = '/dashboard'}
                w="100%"
              >
                Go to Dashboard
              </Button>
            </VStack>
          </VStack>
        </Center>
      );
    }
    
    // Silent redirect
    return <Navigate to={redirectTo || '/dashboard'} replace />;
  }

  // User is authenticated and authorized - render children
  return children;
};

export default ProtectedRoute;
